import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { getChatCompletion } from '../../../services/aiIntegrations/chatCompletion';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';

const PLATFORM_COLORS = {
  facebook: 'text-blue-600 bg-blue-50 border-blue-200',
  instagram: 'text-pink-600 bg-pink-50 border-pink-200',
  google: 'text-orange-600 bg-orange-50 border-orange-200',
  all: 'text-violet-600 bg-violet-50 border-violet-200',
};

const STATUS_CONFIG = {
  active: { label: 'Active', className: 'bg-green-50 text-green-700', dot: 'bg-green-500 animate-pulse' },
  paused: { label: 'Paused', className: 'bg-yellow-50 text-yellow-700', dot: 'bg-yellow-500' },
  draft: { label: 'Draft', className: 'bg-muted text-muted-foreground', dot: 'bg-muted-foreground' },
  completed: { label: 'Completed', className: 'bg-blue-50 text-blue-700', dot: 'bg-blue-500' },
  error: { label: 'Error', className: 'bg-red-50 text-red-700', dot: 'bg-red-500' },
};

const ACTION_BADGE = {
  pause: { label: 'Pause', className: 'bg-red-50 text-red-700 border-red-200' },
  increase_budget: { label: 'Increase Budget', className: 'bg-green-50 text-green-700 border-green-200' },
  decrease_budget: { label: 'Decrease Budget', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  no_change: { label: 'No Change', className: 'bg-muted text-muted-foreground border-border' },
  preview: { label: 'Preview', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  applied: { label: 'Applied', className: 'bg-green-50 text-green-700 border-green-200' },
  failed: { label: 'Failed', className: 'bg-red-50 text-red-700 border-red-200' },
  pending: { label: 'Pending', className: 'bg-muted text-muted-foreground border-border' },
};

const CampaignOptimizerTab = ({ campaigns, onRefresh, organization }) => {
  const { profile } = useAuth();
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [applying, setApplying] = useState(null);
  const [appliedActions, setAppliedActions] = useState({});

  // Google Ads Optimizer state
  const [autoOptimize, setAutoOptimize] = useState(organization?.ai_auto_optimize || false);
  const [savingToggle, setSavingToggle] = useState(false);
  const [runningOptimizer, setRunningOptimizer] = useState(false);
  const [previewingOptimizer, setPreviewingOptimizer] = useState(false);
  const [optimizerResult, setOptimizerResult] = useState(null);
  const [optimizationLogs, setOptimizationLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('google'); // 'google' | 'manual'

  // Sync autoOptimize with organization prop
  useEffect(() => {
    setAutoOptimize(organization?.ai_auto_optimize || false);
  }, [organization?.ai_auto_optimize]);

  // Fetch optimization logs
  const fetchLogs = useCallback(async () => {
    if (!profile?.org_id) return;
    setLogsLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('ai_optimization_logs')
        ?.select('*')
        ?.eq('organization_id', profile?.org_id)
        ?.order('created_at', { ascending: false })
        ?.limit(20);
      if (!error) setOptimizationLogs(data || []);
    } catch (e) {
      // silent
    } finally {
      setLogsLoading(false);
    }
  }, [profile?.org_id]);

  useEffect(() => {
    if (activeSection === 'google') {
      fetchLogs();
    }
  }, [activeSection, fetchLogs]);

  // Toggle auto-optimize
  const handleToggleAutoOptimize = async () => {
    setSavingToggle(true);
    const newVal = !autoOptimize;
    try {
      const { error } = await supabase
        ?.from('organizations')
        ?.update({ ai_auto_optimize: newVal })
        ?.eq('id', profile?.org_id);
      if (error) throw error;
      setAutoOptimize(newVal);
      toast?.success(newVal ? 'Auto-Optimize enabled' : 'Auto-Optimize disabled');
    } catch (e) {
      toast?.error('Failed to update setting');
    } finally {
      setSavingToggle(false);
    }
  };

  // Call google-ads-optimizer edge function
  const callOptimizer = async (dryRun) => {
    if (dryRun) setPreviewingOptimizer(true);
    else setRunningOptimizer(true);
    setOptimizerResult(null);

    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const res = await fetch(`${supabaseUrl}/functions/v1/google-ads-optimizer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'apikey': import.meta.env?.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          organization_id: profile?.org_id,
          dry_run: dryRun,
        }),
      });

      const data = await res?.json();
      if (!res?.ok) throw new Error(data?.error || 'Optimizer failed');

      setOptimizerResult(data);
      if (!dryRun) {
        toast?.success(`Optimization complete — ${data?.actions_taken?.length || 0} actions taken`);
        onRefresh?.();
        fetchLogs();
      } else {
        toast?.success(`Preview ready — ${data?.logs?.length || 0} recommendations`);
      }
    } catch (e) {
      toast?.error(e?.message || 'Optimization failed');
    } finally {
      setRunningOptimizer(false);
      setPreviewingOptimizer(false);
    }
  };

  // ── Manual per-campaign analysis (existing functionality) ──────────────────

  const handleAnalyze = async (campaign) => {
    setSelectedCampaign(campaign);
    setAnalysis(null);
    setAnalyzing(true);
    setAppliedActions({});

    try {
      const ctr = campaign?.impressions > 0
        ? ((campaign?.clicks / campaign?.impressions) * 100)?.toFixed(2)
        : '0.00';
      const cpl = campaign?.leads > 0
        ? (campaign?.spend / campaign?.leads)?.toFixed(2)
        : null;
      const convRate = campaign?.clicks > 0
        ? ((campaign?.leads / campaign?.clicks) * 100)?.toFixed(2)
        : '0.00';

      const systemPrompt = `You are an expert digital advertising strategist. Analyze campaign performance data and return actionable optimization recommendations in valid JSON only. No markdown, no explanation.`;

      const userPrompt = `Analyze this ${campaign?.platform} ad campaign and provide deep optimization recommendations:

Campaign: "${campaign?.name}"
Platform: ${campaign?.platform}
Status: ${campaign?.status}
Daily Budget: $${campaign?.daily_budget}
Location: ${campaign?.location || 'not specified'}
Original Goal: ${campaign?.prompt || 'not specified'}

Performance Metrics:
- Impressions: ${campaign?.impressions?.toLocaleString()}
- Clicks: ${campaign?.clicks?.toLocaleString()}
- CTR: ${ctr}%
- Leads: ${campaign?.leads}
- Spend: $${campaign?.spend}
- Cost Per Lead: ${cpl ? '$' + cpl : 'N/A (no leads yet)'}
- Conversion Rate: ${convRate}%
- ROAS: ${campaign?.roas ? campaign?.roas + 'x' : 'N/A'}

Return ONLY this JSON:
{
  "overallScore": 72,
  "scoreLabel": "Good",
  "summary": "2-3 sentence performance summary",
  "strengths": ["strength1", "strength2"],
  "weaknesses": ["weakness1", "weakness2"],
  "actions": [
    {
      "id": "action_1",
      "priority": "high",
      "category": "budget",
      "title": "Increase daily budget by 20%",
      "description": "Your CTR is above average. Scaling budget will capture more impressions.",
      "expectedImpact": "+15% leads",
      "type": "budget_change",
      "newValue": ${parseFloat(campaign?.daily_budget || 0) * 1.2}
    }
  ],
  "newAdCopySuggestion": {
    "headline": "...",
    "description": "...",
    "callToAction": "..."
  },
  "budgetRecommendation": {
    "current": ${parseFloat(campaign?.daily_budget || 0)},
    "recommended": ${parseFloat(campaign?.daily_budget || 0) * 1.2},
    "reasoning": "..."
  }
}`;

      const response = await getChatCompletion(
        'ANTHROPIC',
        'claude-sonnet-4-5-20250929',
        [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        { max_tokens: 2000, temperature: 0.4 }
      );

      const content = response?.choices?.[0]?.message?.content || '';
      const jsonMatch = content?.match(/\{[\s\S]*\}/);
      if (!jsonMatch) throw new Error('Invalid response format');
      const parsed = JSON.parse(jsonMatch?.[0]);
      setAnalysis(parsed);
    } catch (err) {
      toast?.error('Failed to analyze campaign. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleApplyAction = async (action) => {
    if (!selectedCampaign) return;
    setApplying(action?.id);

    try {
      if (action?.type === 'budget_change' && action?.newValue) {
        if (
          selectedCampaign?.external_campaign_id &&
          organization?.meta_access_token &&
          (selectedCampaign?.platform === 'facebook' || selectedCampaign?.platform === 'instagram' || selectedCampaign?.platform === 'all')
        ) {
          await fetch(`https://graph.facebook.com/v18.0/${selectedCampaign?.external_campaign_id}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              daily_budget: Math.round(action?.newValue * 100),
              access_token: organization?.meta_access_token,
            }),
          });
        }

        const { error } = await supabase
          ?.from('ad_campaigns')
          ?.update({ daily_budget: parseFloat(action?.newValue?.toFixed(2)) })
          ?.eq('id', selectedCampaign?.id);
        if (error) throw error;

        setAppliedActions(prev => ({ ...prev, [action?.id]: true }));
        toast?.success(`Budget updated to $${action?.newValue?.toFixed(2)}/day`);
        onRefresh?.();
      } else if (action?.type === 'regenerate_copy') {
        if (analysis?.newAdCopySuggestion) {
          const existingCopies = selectedCampaign?.ad_copies || [];
          const updatedCopies = [...existingCopies, analysis?.newAdCopySuggestion];
          const { error } = await supabase
            ?.from('ad_campaigns')
            ?.update({ ad_copies: updatedCopies })
            ?.eq('id', selectedCampaign?.id);
          if (error) throw error;
          setAppliedActions(prev => ({ ...prev, [action?.id]: true }));
          toast?.success('New ad copy added to campaign');
          onRefresh?.();
        }
      } else {
        setAppliedActions(prev => ({ ...prev, [action?.id]: true }));
        toast?.success('Recommendation noted');
      }
    } catch (err) {
      toast?.error('Failed to apply optimization');
    } finally {
      setApplying(null);
    }
  };

  const PRIORITY_CONFIG = {
    high: { label: 'High', className: 'bg-red-50 text-red-700 border-red-200' },
    medium: { label: 'Medium', className: 'bg-amber-50 text-amber-700 border-amber-200' },
    low: { label: 'Low', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  };

  const CATEGORY_ICONS = {
    budget: 'DollarSign',
    audience: 'Users',
    creative: 'Palette',
    bidding: 'TrendingUp',
    targeting: 'Target',
    schedule: 'Clock',
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-green-50 border-green-200';
    if (score >= 60) return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return '—';
    }
  };

  // Determine last run status badge
  const lastRunHasActions = optimizationLogs?.length > 0 &&
    optimizationLogs?.[0]?.action_taken !== 'no_change';

  return (
    <div className="space-y-6">
      {/* Section toggle */}
      <div className="flex items-center gap-2 border-b border-border pb-4">
        <button
          onClick={() => setActiveSection('google')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeSection === 'google' ?'bg-violet-600 text-white' :'bg-muted text-muted-foreground hover:text-foreground'
          }`}
        >
          <Icon name="Bot" size={15} />
          Google Ads AI Optimizer
        </button>
        <button
          onClick={() => setActiveSection('manual')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeSection === 'manual' ?'bg-violet-600 text-white' :'bg-muted text-muted-foreground hover:text-foreground'
          }`}
        >
          <Icon name="Sparkles" size={15} />
          Manual Campaign Analysis
        </button>
      </div>

      {/* ── GOOGLE ADS AI OPTIMIZER SECTION ─────────────────────────────── */}
      {activeSection === 'google' && (
        <div className="space-y-6">
          {/* Controls card */}
          <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
              <div>
                <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Bot" size={18} className="text-violet-600" />
                  Google Ads AI Optimizer
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Claude AI analyzes your Google Ads campaigns and applies optimizations automatically
                </p>
              </div>
              {/* Last run status badge */}
              {optimizationLogs?.length > 0 && (
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${
                  lastRunHasActions
                    ? 'bg-green-50 text-green-700 border-green-200' :'bg-muted text-muted-foreground border-border'
                }`}>
                  <div className={`w-1.5 h-1.5 rounded-full ${lastRunHasActions ? 'bg-green-500' : 'bg-muted-foreground'}`} />
                  {lastRunHasActions ? 'Last run: actions taken' : 'Last run: no changes needed'}
                </div>
              )}
            </div>

            {/* Auto-optimize toggle + action buttons */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {/* Auto-Optimize Toggle */}
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl border border-border flex-1">
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">Auto-Optimize</p>
                  <p className="text-xs text-muted-foreground">Automatically apply AI recommendations daily</p>
                </div>
                <button
                  onClick={handleToggleAutoOptimize}
                  disabled={savingToggle}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
                    autoOptimize ? 'bg-violet-600' : 'bg-muted-foreground/30'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      autoOptimize ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                {savingToggle && (
                  <div className="w-4 h-4 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin" />
                )}
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => callOptimizer(true)}
                  disabled={previewingOptimizer || runningOptimizer}
                  className="flex items-center gap-2 px-4 py-2.5 border border-violet-300 text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {previewingOptimizer ? (
                    <div className="w-4 h-4 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin" />
                  ) : (
                    <Icon name="Eye" size={15} />
                  )}
                  Preview Recommendations
                </button>
                <button
                  onClick={() => callOptimizer(false)}
                  disabled={runningOptimizer || previewingOptimizer}
                  className="flex items-center gap-2 px-4 py-2.5 bg-violet-600 text-white hover:bg-violet-700 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {runningOptimizer ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Icon name="Zap" size={15} />
                  )}
                  Run Optimization Now
                </button>
              </div>
            </div>
          </div>

          {/* Optimizer result preview */}
          {optimizerResult && (
            <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Icon name={optimizerResult?.dry_run ? 'Eye' : 'CheckCircle'} size={15} className={optimizerResult?.dry_run ? 'text-blue-500' : 'text-green-500'} />
                  {optimizerResult?.dry_run ? 'Preview Recommendations' : 'Optimization Results'}
                </h4>
                <span className="text-xs text-muted-foreground">
                  {optimizerResult?.campaigns_analyzed} campaigns analyzed
                </span>
              </div>
              {optimizerResult?.logs?.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No campaigns found or no actions needed.</p>
              ) : (
                <div className="space-y-2">
                  {optimizerResult?.logs?.map((log, i) => {
                    const actionBadge = ACTION_BADGE?.[log?.action_taken] || ACTION_BADGE?.pending;
                    return (
                      <div key={i} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg border border-border">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <p className="text-sm font-medium text-foreground truncate">{log?.campaign_name}</p>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${actionBadge?.className}`}>
                              {actionBadge?.label}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">{log?.reason}</p>
                          {log?.old_value && log?.new_value && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {log?.old_value} → <span className="font-medium text-foreground">{log?.new_value}</span>
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Optimization Logs Table */}
          <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Icon name="ClipboardList" size={15} className="text-violet-500" />
                Optimization History
              </h4>
              <button
                onClick={fetchLogs}
                disabled={logsLoading}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                title="Refresh logs"
              >
                <Icon name="RefreshCw" size={13} className={`text-muted-foreground ${logsLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {logsLoading ? (
              <div className="flex items-center justify-center py-10">
                <div className="w-6 h-6 border-2 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
              </div>
            ) : optimizationLogs?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center px-4">
                <Icon name="ClipboardList" size={24} className="text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No optimization runs yet</p>
                <p className="text-xs text-muted-foreground mt-1">Run an optimization to see results here</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Date</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Campaign</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Action</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Reason</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {optimizationLogs?.map((log) => {
                      const statusBadge = ACTION_BADGE?.[log?.status] || ACTION_BADGE?.pending;
                      const actionBadge = ACTION_BADGE?.[log?.action_taken] || ACTION_BADGE?.pending;
                      return (
                        <tr key={log?.id} className="hover:bg-muted/20 transition-colors">
                          <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                            {formatDate(log?.created_at)}
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-foreground truncate max-w-[160px]">
                              {log?.campaign_name || '—'}
                            </p>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${actionBadge?.className}`}>
                              {actionBadge?.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground max-w-[200px]">
                            <span className="line-clamp-2">{log?.reason || '—'}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${statusBadge?.className}`}>
                              {statusBadge?.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── MANUAL CAMPAIGN ANALYSIS SECTION ────────────────────────────── */}
      {activeSection === 'manual' && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left: Campaign list */}
          <div className="lg:col-span-2 space-y-3">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Sparkles" size={16} className="text-violet-600" />
              <h3 className="text-sm font-semibold text-foreground">Select Campaign to Optimize</h3>
            </div>

            {campaigns?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-border rounded-xl">
                <Icon name="Megaphone" size={24} className="text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No campaigns yet</p>
                <p className="text-xs text-muted-foreground mt-1">Create a campaign first</p>
              </div>
            ) : (
              campaigns?.map((campaign) => {
                const statusCfg = STATUS_CONFIG?.[campaign?.status] || STATUS_CONFIG?.draft;
                const platformColor = PLATFORM_COLORS?.[campaign?.platform] || 'text-muted-foreground bg-muted border-border';
                const isSelected = selectedCampaign?.id === campaign?.id;
                const ctr = campaign?.impressions > 0
                  ? ((campaign?.clicks / campaign?.impressions) * 100)?.toFixed(1)
                  : '0.0';

                return (
                  <button
                    key={campaign?.id}
                    onClick={() => handleAnalyze(campaign)}
                    disabled={analyzing && selectedCampaign?.id === campaign?.id}
                    className={`w-full text-left p-4 rounded-xl border transition-all ${
                      isSelected
                        ? 'border-violet-400 bg-violet-50/50 shadow-sm'
                        : 'border-border bg-card hover:border-violet-300 hover:bg-violet-50/20'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-foreground truncate">{campaign?.name}</p>
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${platformColor}`}>
                            {campaign?.platform}
                          </span>
                          <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${statusCfg?.className}`}>
                            <div className={`w-1 h-1 rounded-full ${statusCfg?.dot}`} />
                            {statusCfg?.label}
                          </div>
                        </div>
                      </div>
                      {analyzing && isSelected ? (
                        <div className="w-4 h-4 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin flex-shrink-0 mt-0.5" />
                      ) : (
                        <Icon name="ChevronRight" size={14} className={`flex-shrink-0 mt-0.5 ${isSelected ? 'text-violet-600' : 'text-muted-foreground'}`} />
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="text-center">
                        <p className="text-xs font-bold text-foreground">{campaign?.impressions?.toLocaleString() || '0'}</p>
                        <p className="text-[10px] text-muted-foreground">Impr.</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs font-bold text-foreground">{ctr}%</p>
                        <p className="text-[10px] text-muted-foreground">CTR</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs font-bold text-foreground">${campaign?.daily_budget || '0'}</p>
                        <p className="text-[10px] text-muted-foreground">Budget/d</p>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Right: Analysis panel */}
          <div className="lg:col-span-3">
            {!selectedCampaign && !analyzing ? (
              <div className="flex flex-col items-center justify-center h-full min-h-80 border-2 border-dashed border-border rounded-xl text-center p-8">
                <div className="w-14 h-14 bg-violet-50 rounded-2xl flex items-center justify-center mb-4">
                  <Icon name="Sparkles" size={26} className="text-violet-500" />
                </div>
                <h3 className="text-base font-semibold text-foreground mb-2">Manual Campaign Analyzer</h3>
                <p className="text-sm text-muted-foreground max-w-xs">
                  Select a campaign on the left to get Claude AI's deep analysis and actionable optimization recommendations.
                </p>
              </div>
            ) : analyzing ? (
              <div className="flex flex-col items-center justify-center h-full min-h-80 border border-border rounded-xl p-8">
                <div className="w-12 h-12 border-3 border-violet-200 border-t-violet-600 rounded-full animate-spin mb-4" style={{ borderWidth: '3px' }} />
                <p className="text-sm font-semibold text-foreground mb-1">Claude is analyzing your campaign...</p>
                <p className="text-xs text-muted-foreground">Reviewing metrics, identifying opportunities</p>
              </div>
            ) : analysis ? (
              <div className="space-y-4">
                {/* Score header */}
                <div className={`flex items-center gap-4 p-4 rounded-xl border ${getScoreBg(analysis?.overallScore)}`}>
                  <div className="text-center flex-shrink-0">
                    <p className={`text-3xl font-bold ${getScoreColor(analysis?.overallScore)}`}>{analysis?.overallScore}</p>
                    <p className="text-xs text-muted-foreground font-medium">{analysis?.scoreLabel}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground mb-1">{selectedCampaign?.name}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{analysis?.summary}</p>
                  </div>
                  <button
                    onClick={() => handleAnalyze(selectedCampaign)}
                    className="flex-shrink-0 p-2 rounded-lg border border-border bg-background hover:bg-muted transition-colors"
                    title="Re-analyze"
                  >
                    <Icon name="RefreshCw" size={14} className="text-muted-foreground" />
                  </button>
                </div>

                {/* Strengths & Weaknesses */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
                    <p className="text-xs font-semibold text-green-800 mb-2 flex items-center gap-1">
                      <Icon name="TrendingUp" size={12} />
                      Strengths
                    </p>
                    <ul className="space-y-1">
                      {analysis?.strengths?.map((s, i) => (
                        <li key={i} className="text-xs text-green-700 flex items-start gap-1.5">
                          <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-xs font-semibold text-red-800 mb-2 flex items-center gap-1">
                      <Icon name="TrendingDown" size={12} />
                      Weaknesses
                    </p>
                    <ul className="space-y-1">
                      {analysis?.weaknesses?.map((w, i) => (
                        <li key={i} className="text-xs text-red-700 flex items-start gap-1.5">
                          <span className="text-red-400 mt-0.5 flex-shrink-0">!</span>
                          {w}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Action recommendations */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                    Optimization Actions ({analysis?.actions?.length})
                  </p>
                  <div className="space-y-3">
                    {analysis?.actions?.map((action) => {
                      const priorityCfg = PRIORITY_CONFIG?.[action?.priority] || PRIORITY_CONFIG?.low;
                      const categoryIcon = CATEGORY_ICONS?.[action?.category] || 'Zap';
                      const isApplied = appliedActions?.[action?.id];

                      return (
                        <div
                          key={action?.id}
                          className={`p-4 rounded-xl border transition-all ${
                            isApplied ? 'border-green-200 bg-green-50/50 opacity-70' : 'border-border bg-card'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 min-w-0 flex-1">
                              <div className="w-8 h-8 bg-violet-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Icon name={categoryIcon} size={14} className="text-violet-600" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <p className="text-sm font-semibold text-foreground">{action?.title}</p>
                                  <span className={`px-1.5 py-0.5 rounded border text-[10px] font-medium ${priorityCfg?.className}`}>
                                    {priorityCfg?.label}
                                  </span>
                                </div>
                                <p className="text-xs text-muted-foreground mb-2">{action?.description}</p>
                                {action?.expectedImpact && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-violet-50 text-violet-700 rounded-full text-xs font-medium">
                                    <Icon name="TrendingUp" size={10} />
                                    {action?.expectedImpact}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex-shrink-0">
                              {isApplied ? (
                                <div className="flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs font-medium">
                                  <Icon name="Check" size={12} />
                                  Applied
                                </div>
                              ) : (
                                <button
                                  onClick={() => handleApplyAction(action)}
                                  disabled={applying === action?.id}
                                  className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 text-white rounded-lg text-xs font-medium hover:bg-violet-700 disabled:opacity-50 transition-colors whitespace-nowrap"
                                >
                                  {applying === action?.id ? (
                                    <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />
                                  ) : (
                                    <Icon name="Zap" size={12} />
                                  )}
                                  {action?.type === 'info' ? 'Acknowledge' : 'Apply'}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* New ad copy suggestion */}
                {analysis?.newAdCopySuggestion && (
                  <div className="p-4 bg-card border border-border rounded-xl">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <Icon name="Palette" size={12} className="text-violet-500" />
                      Suggested New Ad Copy
                    </p>
                    <div className="space-y-2">
                      <div>
                        <span className="text-xs text-muted-foreground">Headline: </span>
                        <span className="text-sm font-semibold text-foreground">{analysis?.newAdCopySuggestion?.headline}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{analysis?.newAdCopySuggestion?.description}</p>
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium">
                        <Icon name="MousePointerClick" size={11} />
                        {analysis?.newAdCopySuggestion?.callToAction}
                      </span>
                    </div>
                  </div>
                )}

                {/* Budget recommendation */}
                {analysis?.budgetRecommendation && (
                  <div className="p-4 bg-card border border-border rounded-xl">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <Icon name="DollarSign" size={12} className="text-green-500" />
                      Budget Recommendation
                    </p>
                    <div className="flex items-center gap-4 mb-2">
                      <div>
                        <p className="text-xs text-muted-foreground">Current</p>
                        <p className="text-lg font-bold text-foreground">${analysis?.budgetRecommendation?.current?.toFixed(2)}/day</p>
                      </div>
                      <Icon name="ArrowRight" size={16} className="text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Recommended</p>
                        <p className="text-lg font-bold text-green-600">${analysis?.budgetRecommendation?.recommended?.toFixed(2)}/day</p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">{analysis?.budgetRecommendation?.reasoning}</p>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignOptimizerTab;
